import { Trans, useLingui } from '@lingui/react/macro'

import type { MusicMetadataSuccess } from '@/pages/tad.ai/_components/MusicItem/_components/InternalMusicItem/_components/MusicItemSuccess'

import { useNormalizePublicSrc } from '@/hooks/public'
import LazyImage from '@/components/LazyImage'
import MusicOptions from '@/pages/tad.ai/_components/MusicItem/_components/MusicOptions'
import { fallbackImg } from '@/constants/assets'
import MusicDescription from '@/components/MusicProfile/_components/MusicDescription'
import MusicProfile from '@/components/MusicProfile'
import Avatar from '@/pages/tad.ai/_components/Avatar'


import { cls } from 'tagged-classnames-free';
interface BannerProps {
  data: MusicMetadataSuccess
  className?: string
}

const Banner: React.FC<BannerProps> = (props) => {
  const { data, className } = props
  const { t } = useLingui()
  const normalizePublicSrc = useNormalizePublicSrc()

  return (
    <div
      className={cls`flex flex-col items-center justify-start gap-6 md:gap-8 ${className}`}
    >
      <div
        className={cls`flex w-full gap-2 transition-all duration-300 md:gap-5 lg:flex-col lg:items-center`}
      >
        <div
          className={cls`
            border-f-bg-active size-12 min-w-12 border md:size-16 lg:size-[248px] 
            lg:border-[10px] [&_img]:size-full [&_img]:object-cover
          `}
        >
          <LazyImage
            fill={false}
            src={data.cover || fallbackImg}
            onError={(el: any) => {
              el.currentTarget.onerror = null
              el.currentTarget.src = normalizePublicSrc(fallbackImg)
            }}
            width={248}
            height={248}
            alt={'name'}
          />
        </div>
        <div className='flex w-full gap-1'>
          <MusicProfile
            extra={{ avatar: { size: 24 } }}
            showTypes={['userInfo']}
            data={data}
            cover={{ className: 'hidden' }}
            className={cls`flex-1 [&_a[href]]:text-2xl`}
          />
          <MusicOptions
            data={data}
            onlyExpand
            expand={['liked']}
            className={cls`flex-none`}
            btnClassName={cls`hover:!text-f-primary-hover flex !flex-row-reverse !border-transparent`}
            iconClassName={cls`size-6`}
          />
          <MusicOptions
            data={data}
            onlyExpand
            expand={[{ key: 'share', iconOnly: true }]}
            className={cls`flex-none`}
            iconClassName={cls`size-6`}
          />
        </div>
      </div>
      <div className={cls`flex w-full flex-col items-stretch gap-5`}>
        {[
          {
            key: 'Idea',
            title: <Trans>Idea</Trans>,
            content: data.lyricsPrompt || t`No lyrics prompt`,
          },
          {
            key: 'Prompt',
            title: <Trans>Prompt</Trans>,
            content: data.description || t`No style prompt`,
          },
        ].map((item, index) =>
          item.content ? (
            <div key={item.key} className='text-f-text-secondary flex flex-col'>
              <div>{item.title}</div>
              <MusicDescription
                className={cls`line-clamp-1 text-sm lg:line-clamp-none`}
              >
                {item.content}
              </MusicDescription>
            </div>
          ) : null,
        )}
        {data.coverAuthor && (
          <div className='text-f-text-secondary flex flex-col'>
            <div>
              <Trans>Cover</Trans>
            </div>
            <div
              className={cls`flex gap-1 [&_img]:size-full [&_img]:object-cover`}
            >
              <Avatar
                className={cls`size-6`}
                size={24}
                url={data.coverAuthor?.image}
                name={data.coverAuthor?.name || ''}
              />
              <span
                className={cls`text-f-text-quaternary line-clamp-1 text-sm lg:line-clamp-none`}
              >
                {data.coverAuthor?.name}
              </span>
            </div>
          </div>
        )}
        {/* <Button
          type='primary'
          className={cls`self-start rounded-[60px]`}
        >
          Recreate
        </Button> */}
      </div>
    </div>
  )
}

export default Banner
